import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase/config';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  doc, 
  where 
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const JournalEntries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [printDateRange, setPrintDateRange] = useState({ start: '', end: '' });

  // Fetch all data in parallel (QUOTA FRIENDLY - fixed number of reads)
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch ALL needed collections in parallel - this is the KEY optimization
      const [journalSnapshot, clientsSnapshot, ordersSnapshot, paymentsSnapshot, itemsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'journalEntries'), orderBy('date', 'desc'))),
        getDocs(collection(db, 'clients')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'payments')),
        getDocs(collection(db, 'orderItems'))
      ]);

      // Build maps for fast lookups (O(1) instead of O(n) per entry)
      const clientsMap = new Map();
      clientsSnapshot.docs.forEach(doc => {
        clientsMap.set(doc.id, { id: doc.id, ...doc.data() });
      });
      setClients(Array.from(clientsMap.values()));

      const ordersMap = new Map();
      ordersSnapshot.docs.forEach(doc => {
        ordersMap.set(doc.id, { id: doc.id, ...doc.data() });
      });

      const paymentsMap = new Map();
      paymentsSnapshot.docs.forEach(doc => {
        paymentsMap.set(doc.id, { id: doc.id, ...doc.data() });
      });

      const orderItemsMap = new Map();
      itemsSnapshot.docs.forEach(doc => {
        const item = doc.data();
        const orderId = item.orderId;
        if (!orderItemsMap.has(orderId)) {
          orderItemsMap.set(orderId, []);
        }
        orderItemsMap.get(orderId).push(item);
      });

      // Enrich entries using cached data (NO EXTRA DATABASE READS!)
      const enrichedEntries = journalSnapshot.docs.map(doc => {
        const entry = { id: doc.id, ...doc.data() };
        let clientName = null;
        let clientPhone = null;
        let clientId = null;
        let orderNumber = null;
        let products = [];

        const payment = paymentsMap.get(entry.transactionId);
        if (payment) {
          clientId = payment.clientId;
          const client = clientsMap.get(payment.clientId);
          if (client) {
            clientName = client.name;
            clientPhone = client.phone;
          }
          const order = ordersMap.get(payment.orderId);
          if (order) {
            orderNumber = order.orderNumber;
          }
        }

        // If no payment found, try as order
        if (!clientId && ordersMap.has(entry.transactionId)) {
          const order = ordersMap.get(entry.transactionId);
          orderNumber = order.orderNumber;
          clientId = order.clientId;
          const client = clientsMap.get(order.clientId);
          if (client) {
            clientName = client.name;
            clientPhone = client.phone;
          }

          const orderItems = orderItemsMap.get(entry.transactionId) || [];
          products = orderItems.slice(0, 3).map(item => `${item.productName} x${item.quantity}`);
        }

        return {
          ...entry,
          clientName,
          clientPhone,
          clientId,
          orderNumber,
          products,
          productCount: products.length
        };
      });

      setEntries(enrichedEntries);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter entries (client-side - no extra reads)
  const filterEntries = useCallback(() => {
    let filtered = [...entries];
    
    // Filter by client
    if (selectedClient !== 'all') {
      filtered = filtered.filter(entry => entry.clientId === selectedClient);
    }
    
    // Filter by date range
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate;
      switch (dateRange) {
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case 'year':
          startDate = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
        default:
          startDate = null;
      }
      if (startDate) {
        filtered = filtered.filter(entry => {
          const entryDate = entry.date?.toDate();
          return entryDate && entryDate >= startDate;
        });
      }
    }
    
    return filtered;
  }, [entries, selectedClient, dateRange]);

  const filteredEntries = useMemo(() => filterEntries(), [filterEntries]);

  // Pagination for filtered entries
  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredEntries.slice(startIndex, endIndex);
  }, [filteredEntries, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);

  const getTotalDebits = (entry) => {
    if (!entry.entries) return 0;
    return entry.entries.reduce((sum, e) => sum + (e.debit || 0), 0);
  };

  const getTotalCredits = (entry) => {
    if (!entry.entries) return 0;
    return entry.entries.reduce((sum, e) => sum + (e.credit || 0), 0);
  };

  const formatDescription = useCallback((entry) => {
    const parts = [];
    
    if (entry.clientName) {
      parts.push(`Client: ${entry.clientName}`);
      if (entry.clientPhone) parts.push(`(${entry.clientPhone})`);
    }
    
    if (entry.orderNumber) {
      parts.push(`Order: ${entry.orderNumber}`);
    }
    
    if (entry.products && entry.products.length > 0) {
      parts.push(`Items: ${entry.products.join(', ')}`);
      if (entry.productCount > 3) parts.push(`+${entry.productCount - 3} more`);
    }
    
    if (parts.length === 0 && entry.entries && entry.entries[0]?.description) {
      parts.push(entry.entries[0].description);
    }
    
    if (parts.length === 0 && entry.transactionType) {
      parts.push(`${entry.transactionType} transaction`);
    }
    
    return parts.join(' · ') || 'No description';
  }, []);

  const printJournal = (filteredOnly = true) => {
    const entriesToPrint = filteredOnly ? filteredEntries : entries;
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFillColor(127, 26, 46);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('GENERAL JOURNAL', pageWidth / 2, 25, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 50);
    
    // Filter info
    let filterInfo = '';
    if (selectedClient !== 'all') {
      const client = clients.find(c => c.id === selectedClient);
      filterInfo = `Client: ${client?.name || 'Selected Client'}`;
    }
    if (dateRange !== 'all') {
      filterInfo += filterInfo ? ` · Date: ${dateRange.toUpperCase()}` : `Date: ${dateRange.toUpperCase()}`;
    }
    if (filterInfo) {
      doc.text(filterInfo, 20, 57);
    }
    doc.text(`Total Entries: ${entriesToPrint.length}`, 20, filterInfo ? 64 : 57);
    
    // Table data
    const tableData = entriesToPrint.map(entry => {
      const totalDebit = getTotalDebits(entry);
      const totalCredit = getTotalCredits(entry);
      const description = formatDescription(entry);
      
      return [
        entry.date?.toDate ? new Date(entry.date.toDate()).toLocaleDateString() : 'N/A',
        entry.orderNumber || entry.transactionId?.slice(-8) || 'N/A',
        description || entry.transactionType || 'N/A',
        entry.transactionType === 'payment' ? 'Payment' : entry.transactionType === 'sale' ? 'Sale' : 'COGS',
        entry.entries?.map(e => e.accountName || e.account).join(', ') || '-',
        totalDebit.toLocaleString(),
        totalCredit.toLocaleString(),
        totalDebit === totalCredit ? 'Balanced' : 'Unbalanced'
      ];
    });
    
    autoTable(doc, {
      startY: filterInfo ? 70 : 65,
      head: [['Date', 'Reference', 'Description', 'Type', 'Accounts', 'Debit (UGX)', 'Credit (UGX)', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [127, 26, 46],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 30 },
        2: { cellWidth: 55 },
        3: { cellWidth: 20 },
        4: { cellWidth: 45 },
        5: { cellWidth: 25, halign: 'right' },
        6: { cellWidth: 25, halign: 'right' },
        7: { cellWidth: 20, halign: 'center' }
      }
    });
    
    // Summary
    const finalY = doc.lastAutoTable.finalY + 10;
    const totalDebitSum = entriesToPrint.reduce((sum, e) => sum + getTotalDebits(e), 0);
    const totalCreditSum = entriesToPrint.reduce((sum, e) => sum + getTotalCredits(e), 0);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('SUMMARY', 20, finalY);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Debits: ${totalDebitSum.toLocaleString()} UGX`, 20, finalY + 8);
    doc.text(`Total Credits: ${totalCreditSum.toLocaleString()} UGX`, 20, finalY + 16);
    doc.text(`Difference: ${(totalDebitSum - totalCreditSum).toLocaleString()} UGX`, 20, finalY + 24);
    
    if (totalDebitSum === totalCreditSum) {
      doc.setTextColor(10, 185, 129);
      doc.text('✓ All entries are balanced', 20, finalY + 34);
    } else {
      doc.setTextColor(239, 68, 68);
      doc.text('⚠️ Warning: Total debits do not equal total credits', 20, finalY + 34);
    }
    
    doc.save(`General_Journal_${new Date().toISOString().split('T')[0]}.pdf`);
    setShowPrintModal(false);
  };

  const totalDebitSum = filteredEntries.reduce((sum, e) => sum + getTotalDebits(e), 0);
  const totalCreditSum = filteredEntries.reduce((sum, e) => sum + getTotalCredits(e), 0);
  const isBalanced = totalDebitSum === totalCreditSum;

  if (loading) {
    return React.createElement(
      'div',
      { style: localStyles.loadingContainer },
      React.createElement('div', { style: localStyles.spinner }),
      React.createElement('p', null, 'Loading journal entries...')
    );
  }

  if (error) {
    return React.createElement(
      'div',
      { style: localStyles.errorContainer },
      React.createElement('p', { style: localStyles.errorText }, 'Error loading journal entries: ', error),
      React.createElement(
        'button',
        { style: localStyles.retryButton, onClick: fetchAllData },
        'Retry'
      )
    );
  }

  return React.createElement(
    'div',
    { style: localStyles.container },
    React.createElement(
      'div',
      { style: localStyles.header },
      React.createElement(
        'div',
        null,
        React.createElement('h3', { style: localStyles.title }, 'General Journal'),
        React.createElement('p', { style: localStyles.subtitle }, `All accounting entries with double-entry verification (${filteredEntries.length} entries)`)
      ),
      React.createElement(
        'div',
        { style: localStyles.headerActions },
        React.createElement(
          'button',
          { style: localStyles.printButton, onClick: () => setShowPrintModal(true) },
          '🖨️ Print Journal'
        ),
        React.createElement(
          'button',
          { style: localStyles.printButtonClient, onClick: () => printJournal(true) },
          '📄 Print Current View'
        )
      )
    ),
    
    // Filters
    React.createElement(
      'div',
      { style: localStyles.filtersBar },
      React.createElement(
        'div',
        { style: localStyles.filterGroup },
        React.createElement('label', { style: localStyles.filterLabel }, 'Client'),
        React.createElement(
          'select',
          { style: localStyles.filterSelect, value: selectedClient, onChange: (e) => { setSelectedClient(e.target.value); setCurrentPage(1); } },
          React.createElement('option', { value: 'all' }, 'All Clients'),
          clients.map(client => React.createElement('option', { key: client.id, value: client.id }, client.name))
        )
      ),
      React.createElement(
        'div',
        { style: localStyles.filterGroup },
        React.createElement('label', { style: localStyles.filterLabel }, 'Date Range'),
        React.createElement(
          'select',
          { style: localStyles.filterSelect, value: dateRange, onChange: (e) => { setDateRange(e.target.value); setCurrentPage(1); } },
          React.createElement('option', { value: 'all' }, 'All Time'),
          React.createElement('option', { value: 'week' }, 'Last 7 Days'),
          React.createElement('option', { value: 'month' }, 'Last 30 Days'),
          React.createElement('option', { value: 'year' }, 'Last 12 Months')
        )
      ),
      React.createElement(
        'div',
        { style: localStyles.summaryBadge },
        React.createElement('span', { style: localStyles.summaryText }, `Total Debit: ${totalDebitSum.toLocaleString()} UGX`),
        React.createElement('span', { style: localStyles.summaryText }, `Total Credit: ${totalCreditSum.toLocaleString()} UGX`),
        React.createElement('span', { style: { ...localStyles.summaryText, ...(isBalanced ? localStyles.balancedText : localStyles.unbalancedText) } },
          isBalanced ? '✓ Balanced' : '✗ Unbalanced'
        )
      )
    ),
    
    // Transactions Table
    React.createElement(
      'div',
      { style: localStyles.tableWrapper },
      React.createElement(
        'table',
        { style: localStyles.table },
        React.createElement(
          'thead',
          null,
          React.createElement(
            'tr',
            { style: localStyles.tableHeaderRow },
            React.createElement('th', { style: localStyles.tableHeader }, 'Date'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Reference'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Description'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Type'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Accounts'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Debit'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Credit'),
            React.createElement('th', { style: localStyles.tableHeader }, 'Status'),
            React.createElement('th', { style: localStyles.tableHeader }, '')
          )
        ),
        React.createElement(
          'tbody',
          null,
          filteredEntries.length === 0 ? React.createElement(
            'tr',
            null,
            React.createElement('td', { colSpan: '9', style: localStyles.emptyState }, 'No journal entries match the selected filters')
          ) : paginatedEntries.map(entry => {
            const totalDebit = getTotalDebits(entry);
            const totalCredit = getTotalCredits(entry);
            const isEntryBalanced = totalDebit === totalCredit;
            const description = formatDescription(entry);
            
            return React.createElement(
              React.Fragment,
              { key: entry.id },
              React.createElement(
                'tr',
                { 
                  style: localStyles.tableRow,
                  onClick: () => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)
                },
                React.createElement('td', { style: localStyles.tableCell }, entry.date?.toDate ? new Date(entry.date.toDate()).toLocaleDateString() : 'N/A'),
                React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.transactionId } },
                  entry.orderNumber || entry.transactionId?.slice(-8) || 'N/A'
                ),
                React.createElement('td', { style: localStyles.tableCell },
                  React.createElement('div', { style: localStyles.descriptionText }, description)
                ),
                React.createElement(
                  'td',
                  { style: localStyles.tableCell },
                  React.createElement('span', { style: { ...localStyles.typeBadge, ...localStyles[`type${entry.transactionType}`] } },
                    entry.transactionType === 'payment' ? 'Payment' : 
                    entry.transactionType === 'sale' ? 'Sale' : 
                    entry.transactionType === 'cogs' ? 'COGS' :
                    entry.transactionType || 'Unknown'
                  )
                ),
                React.createElement(
                  'td',
                  { style: localStyles.tableCell },
                  React.createElement(
                    'div',
                    { style: localStyles.accountsList },
                    entry.entries && entry.entries.slice(0, 3).map((e, idx) => React.createElement('span', { key: idx, style: localStyles.accountTag },
                      e.accountName || e.account
                    )),
                    entry.entries?.length > 3 && React.createElement('span', { style: localStyles.accountTag }, `+${entry.entries.length - 3} more`)
                  )
                ),
                React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.debitCell } },
                  totalDebit.toLocaleString()
                ),
                React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.creditCell } },
                  totalCredit.toLocaleString()
                ),
                React.createElement(
                  'td',
                  { style: localStyles.tableCell },
                  isEntryBalanced 
                    ? React.createElement('span', { style: localStyles.balancedBadge }, '✓')
                    : React.createElement('span', { style: localStyles.unbalancedBadge }, '✗')
                ),
                React.createElement(
                  'td',
                  { style: localStyles.tableCell },
                  React.createElement('button', { style: localStyles.expandButton },
                    expandedEntry === entry.id ? '▲' : '▼'
                  )
                )
              ),
              
              expandedEntry === entry.id && React.createElement(
                'tr',
                null,
                React.createElement(
                  'td',
                  { colSpan: '9', style: localStyles.expandedCell },
                  React.createElement(
                    'div',
                    { style: localStyles.expandedContent },
                    React.createElement(
                      'div',
                      { style: localStyles.expandedHeader },
                      React.createElement('h4', { style: localStyles.expandedTitle }, 'Journal Entry Details'),
                      React.createElement('p', { style: localStyles.createdAt },
                        'Created: ', entry.createdAt?.toDate ? new Date(entry.createdAt.toDate()).toLocaleString() : 'N/A'
                      )
                    ),
                    (entry.clientName || entry.orderNumber) && React.createElement(
                      'div',
                      { style: localStyles.clientInfoBox },
                      entry.clientName && React.createElement(
                        'div',
                        { style: localStyles.clientInfoRow },
                        React.createElement('span', { style: localStyles.clientInfoLabel }, 'Client:'),
                        React.createElement('span', { style: localStyles.clientInfoValue }, entry.clientName)
                      ),
                      entry.clientPhone && React.createElement(
                        'div',
                        { style: localStyles.clientInfoRow },
                        React.createElement('span', { style: localStyles.clientInfoLabel }, 'Contact:'),
                        React.createElement('span', { style: localStyles.clientInfoValue }, entry.clientPhone)
                      ),
                      entry.orderNumber && React.createElement(
                        'div',
                        { style: localStyles.clientInfoRow },
                        React.createElement('span', { style: localStyles.clientInfoLabel }, 'Order:'),
                        React.createElement('span', { style: localStyles.clientInfoValue }, entry.orderNumber)
                      )
                    ),
                    React.createElement(
                      'table',
                      { style: localStyles.detailTable },
                      React.createElement(
                        'thead',
                        null,
                        React.createElement(
                          'tr',
                          null,
                          React.createElement('th', { style: localStyles.detailHeader }, 'Account'),
                          React.createElement('th', { style: localStyles.detailHeader }, 'Account Name'),
                          React.createElement('th', { style: localStyles.detailHeader }, 'Description'),
                          React.createElement('th', { style: localStyles.detailHeader }, 'Debit (UGX)'),
                          React.createElement('th', { style: localStyles.detailHeader }, 'Credit (UGX)')
                        )
                      ),
                      React.createElement(
                        'tbody',
                        null,
                        entry.entries && entry.entries.map((e, idx) => React.createElement(
                          'tr',
                          { key: idx },
                          React.createElement('td', { style: localStyles.detailCell }, React.createElement('code', null, e.account)),
                          React.createElement('td', { style: localStyles.detailCell }, e.accountName || '-'),
                          React.createElement('td', { style: localStyles.detailCell }, e.description || '-'),
                          React.createElement('td', { style: { ...localStyles.detailCell, ...localStyles.debitCell } },
                            e.debit > 0 ? e.debit.toLocaleString() : '-'
                          ),
                          React.createElement('td', { style: { ...localStyles.detailCell, ...localStyles.creditCell } },
                            e.credit > 0 ? e.credit.toLocaleString() : '-'
                          )
                        ))
                      )
                    ),
                    React.createElement(
                      'div',
                      { style: localStyles.totalRowCompact },
                      React.createElement('span', { style: localStyles.totalLabel }, 'Total:'),
                      React.createElement('span', { style: localStyles.totalDebit }, `Debit: ${totalDebit.toLocaleString()} UGX`),
                      React.createElement('span', { style: localStyles.totalCredit }, `Credit: ${totalCredit.toLocaleString()} UGX`),
                      React.createElement('span', { style: isEntryBalanced ? localStyles.balancedText : localStyles.unbalancedText },
                        isEntryBalanced ? '✓ Balanced' : '✗ Unbalanced'
                      )
                    )
                  )
                )
              )
            );
          })
        )
      )
    ),
    
    // Pagination
    totalPages > 1 && React.createElement(
      'div',
      { style: localStyles.pagination },
      React.createElement(
        'button', 
        { 
          style: { ...localStyles.pageButton, ...(currentPage === 1 ? localStyles.pageButtonDisabled : {}) },
          onClick: () => setCurrentPage(prev => Math.max(1, prev - 1)),
          disabled: currentPage === 1
        },
        'Previous'
      ),
      React.createElement('span', { style: localStyles.pageInfo }, `Page ${currentPage} of ${totalPages}`),
      React.createElement(
        'button', 
        { 
          style: { ...localStyles.pageButton, ...(currentPage === totalPages ? localStyles.pageButtonDisabled : {}) },
          onClick: () => setCurrentPage(prev => Math.min(totalPages, prev + 1)),
          disabled: currentPage === totalPages
        },
        'Next'
      )
    ),
    
    // Print Modal
    showPrintModal && React.createElement(
      'div',
      { style: modalStyles.overlay, onClick: () => setShowPrintModal(false) },
      React.createElement(
        'div',
        { style: modalStyles.container, onClick: e => e.stopPropagation() },
        React.createElement(
          'div',
          { style: modalStyles.header },
          React.createElement('h3', { style: modalStyles.title }, 'Print Journal'),
          React.createElement('button', { style: modalStyles.closeButton, onClick: () => setShowPrintModal(false) }, '×')
        ),
        React.createElement(
          'div',
          { style: modalStyles.form },
          React.createElement(
            'div',
            { style: modalStyles.formGroup },
            React.createElement('label', { style: modalStyles.label }, 'Print Options'),
            React.createElement(
              'button',
              { style: modalStyles.optionButton, onClick: () => printJournal(true) },
              '📄 Print Current View (Filtered)'
            ),
            React.createElement(
              'button',
              { style: modalStyles.optionButton, onClick: () => printJournal(false) },
              '📋 Print All Journal Entries'
            )
          ),
          selectedClient !== 'all' && React.createElement(
            'div',
            { style: modalStyles.infoBox },
            React.createElement('span', { style: modalStyles.infoIcon }, 'ℹ️'),
            React.createElement('span', null, `Printing entries for client: ${clients.find(c => c.id === selectedClient)?.name}`)
          ),
          React.createElement(
            'div',
            { style: modalStyles.actions },
            React.createElement('button', { style: modalStyles.buttonSecondary, onClick: () => setShowPrintModal(false) }, 'Cancel')
          )
        )
      )
    )
  );
};

// Styles (EXACTLY as you had them - no changes)
const localStyles = {
  container: {
    padding: '28px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#64748b'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#ef4444'
  },
  errorText: {
    fontSize: '14px',
    marginBottom: '16px'
  },
  retryButton: {
    padding: '8px 16px',
    background: '#7f1a2e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#7f1a2e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  headerActions: {
    display: 'flex',
    gap: '12px'
  },
  printButton: {
    padding: '8px 16px',
    background: '#7f1a2e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  printButtonClient: {
    padding: '8px 16px',
    background: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b'
  },
  filtersBar: {
    display: 'flex',
    gap: '20px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569'
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    background: '#ffffff',
    cursor: 'pointer'
  },
  summaryBadge: {
    display: 'flex',
    gap: '20px',
    padding: '8px 16px',
    background: '#f8fafc',
    borderRadius: '10px',
    fontSize: '12px',
    marginLeft: 'auto'
  },
  summaryText: {
    color: '#475569'
  },
  balancedText: {
    color: '#10b981'
  },
  unbalancedText: {
    color: '#ef4444'
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '12px',
    border: '1px solid #eef2f6'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  tableHeaderRow: {
    background: '#f8fafc',
    borderBottom: '1px solid #eef2f6'
  },
  tableHeader: {
    textAlign: 'left',
    padding: '14px 16px',
    fontWeight: '600',
    color: '#475569'
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    transition: 'background 0.2s ease'
  },
  tableCell: {
    padding: '14px 16px',
    color: '#334155'
  },
  transactionId: {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#64748b'
  },
  descriptionText: {
    fontSize: '12px',
    color: '#334155',
    maxWidth: '350px',
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  accountsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  accountTag: {
    background: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    color: '#475569'
  },
  typeBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500'
  },
  typepayment: {
    background: '#dbeafe',
    color: '#2563eb'
  },
  typesale: {
    background: '#d1fae5',
    color: '#059669'
  },
  typecogs: {
    background: '#fed7aa',
    color: '#b45309'
  },
  debitCell: {
    color: '#10b981',
    fontWeight: '500'
  },
  creditCell: {
    color: '#ef4444',
    fontWeight: '500'
  },
  balancedBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
    background: '#d1fae5',
    color: '#059669'
  },
  unbalancedBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
    background: '#fee2e2',
    color: '#dc2626'
  },
  expandButton: {
    background: 'none',
    border: 'none',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#94a3b8',
    padding: '4px'
  },
  expandedCell: {
    padding: '20px',
    background: '#fafcff'
  },
  expandedContent: {
    padding: '8px'
  },
  expandedHeader: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px'
  },
  expandedTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b'
  },
  createdAt: {
    fontSize: '11px',
    color: '#94a3b8'
  },
  clientInfoBox: {
    background: '#f8fafc',
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '16px',
    border: '1px solid #eef2f6'
  },
  clientInfoRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '6px',
    fontSize: '12px'
  },
  clientInfoLabel: {
    fontWeight: '600',
    color: '#475569',
    width: '70px'
  },
  clientInfoValue: {
    color: '#1e293b',
    fontWeight: '500'
  },
  detailTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px'
  },
  detailHeader: {
    textAlign: 'left',
    padding: '8px 12px',
    background: '#f8fafc',
    fontWeight: '600',
    color: '#475569'
  },
  detailCell: {
    padding: '8px 12px',
    borderBottom: '1px solid #f1f5f9',
    color: '#475569'
  },
  totalRowCompact: {
    marginTop: '16px',
    padding: '12px',
    background: '#f8fafc',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '24px',
    fontSize: '12px',
    fontWeight: '500'
  },
  totalLabel: {
    color: '#475569'
  },
  totalDebit: {
    color: '#10b981'
  },
  totalCredit: {
    color: '#ef4444'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: '#94a3b8',
    fontSize: '13px'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '24px',
    padding: '16px'
  },
  pageButton: {
    padding: '8px 16px',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  pageInfo: {
    fontSize: '13px',
    color: '#64748b'
  }
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  container: {
    background: '#ffffff',
    borderRadius: '24px',
    width: '90%',
    maxWidth: '450px',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eef2f6'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#94a3b8'
  },
  form: {
    padding: '24px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '8px'
  },
  optionButton: {
    width: '100%',
    padding: '12px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginBottom: '12px',
    transition: 'all 0.2s ease'
  },
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: '#fef3c7',
    borderRadius: '10px',
    marginBottom: '20px'
  },
  infoIcon: {
    fontSize: '18px'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px'
  },
  buttonSecondary: {
    flex: 1,
    background: '#f1f5f9',
    color: '#334155',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default JournalEntries;